import { useCallback, useEffect, useState } from 'react'
import { cacheIssue, getLatestCachedIssue, initializeLocalDataCompatibility } from '../data/db'
import type { DailyIssue } from '../types/daily'

interface IssueLoadState {
  issue?: DailyIssue
  loading: boolean
  source?: 'network' | 'cache'
  warning?: string
}

export function useIssue() {
  const [state, setState] = useState<IssueLoadState>({ loading: true })

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: !current.issue }))
    try {
      await initializeLocalDataCompatibility()
      const response = await fetch('/data/latest.json', { cache: 'no-store' })
      if (!response.ok) throw new Error(`request failed (${response.status})`)
      const candidate = (await response.json()) as unknown
      const { validateDailyIssue } = await import('../data/validateDailyIssue')
      const result = validateDailyIssue(candidate)
      if (!result.valid || !result.data) throw new Error(`invalid issue: ${result.errors[0] ?? 'unknown error'}`)
      await cacheIssue(result.data)
      setState({ issue: result.data, loading: false, source: 'network' })
    } catch (error) {
      const cached = await getLatestCachedIssue()
      console.error('Daily issue load failed', error)
      if (cached) {
        setState({ issue: cached, loading: false, source: 'cache', warning: '网络暂时不可用，正在显示上次保存的日报。' })
      } else {
        setState({ loading: false, warning: '暂时无法获取日报，请检查网络后重试。' })
      }
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return { ...state, refresh: load }
}
